import React, { useState, useMemo, useEffect } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  TextField,
  Box,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { SpecialityItem, ValidationErrors, Category, SpecialityCategoriesReducerAction, validationErrors } from '../../speciality';
import { ChevronRight, Close, Search } from '@mui/icons-material';

interface StepSpecialityProps {
  specialities: SpecialityItem[];
  specialityCategories: Category[];
  errors: ValidationErrors;
  specialityCategoriesToVerifyDeletion: string[];
  setErrors: React.Dispatch<React.SetStateAction<ValidationErrors>>;
  dispatch: React.Dispatch<{ type: SpecialityCategoriesReducerAction; value: any }>;
}

const StepSpeciality = ({ specialities, specialityCategories, specialityCategoriesToVerifyDeletion, errors, setErrors, dispatch }: StepSpecialityProps) => {
  const [search, setSearch] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  const [toBeDeleted, setToBeDeleted] = useState<string[]>([]);

  const handleToggle = (category: Category) => {
    const exists = specialityCategories.some((c) => c.id === category.id);

    if (exists && specialityCategoriesToVerifyDeletion.includes(category.id)) {
      setToBeDeleted(() => [category.id]);
      return;
    }

    const updated = exists ? specialityCategories.filter((c) => c.id !== category.id) : [...specialityCategories, category];

    dispatch({ type: SpecialityCategoriesReducerAction.CATEGORIES, value: updated });
  };

  const handleClearAll = () => {
    if (specialityCategoriesToVerifyDeletion.length > 0) {
      setToBeDeleted(specialityCategories.map((c) => c.id));
      return;
    }

    dispatch({ type: SpecialityCategoriesReducerAction.CATEGORIES, value: [] });
  };

  const filteredSpecialities = useMemo(() => {
    if (!search.trim()) return specialities;

    const lower = search.toLowerCase();
    const result = specialities
      .map((spec) => {
        const matchedCategories = spec.categories.filter((cat) => cat.name.toLowerCase().includes(lower));
        return matchedCategories.length ? { ...spec, categories: matchedCategories } : null;
      })
      .filter(Boolean) as SpecialityItem[];

    return result;
  }, [search, specialities]);

  const isGroupExpanded = (id: string) => {
    if (search.trim()) {
      return filteredSpecialities.some((s) => s.id === id);
    }
    return expandedGroups.includes(id);
  };

  const handleAccordionToggle = (id: string) => {
    setExpandedGroups((prev) => (prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]));
  };

  useEffect(() => {
    setErrors((prev) => ({ ...prev, specialityCategories: specialityCategories.length > 0 ? false : validationErrors.id }));
  }, [specialityCategories.length, setErrors]);

  return (
    <Box>
      <Typography variant="h4">Specialities</Typography>
      <TextField
        InputProps={{ endAdornment: <Search sx={{ fill: (theme) => theme.palette.grey[600] }} /> }}
        fullWidth
        variant="outlined"
        placeholder="Type to filter speciality categories…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        margin="normal"
      />

      {!!specialityCategories.length && (
        <Box mt={2} px={2} pb={2} pt={1} sx={{ border: '1px solid #eee', borderRadius: 2, backgroundColor: (theme) => theme.palette.primary.neutral }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              SELECTED
            </Typography>
            <Button startIcon={<Close />} color="error" size="small" onClick={handleClearAll}>
              CLEAR ALL
            </Button>
          </Box>
          <Box display="flex" flexDirection="column" gap={1}>
            {specialityCategories.map((cat) => (
              <FormControlLabel
                key={cat.id}
                control={<Checkbox checked onChange={() => handleToggle(cat)} />}
                label={
                  <Box>
                    <Typography fontSize={16}>{cat.name}</Typography>
                    <Typography fontSize={12} color="text.secondary">
                      {cat.description}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </Box>
        </Box>
      )}
      <Box mt={2}>
        {filteredSpecialities.map((speciality) => (
          <Accordion
            sx={{ mt: '0 !important', mb: '0 !important' }}
            key={speciality.id}
            expanded={isGroupExpanded(speciality.id)}
            onChange={() => handleAccordionToggle(speciality.id)}>
            <AccordionSummary
              sx={{ flexDirection: 'row-reverse', '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': { transform: 'rotate(90deg)' } }}
              expandIcon={<ChevronRight />}>
              <Typography sx={{ ml: 1 }} variant="subtitle2" fontWeight={600}>
                {speciality.name}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {speciality.categories.map((category) => (
                <Box
                  sx={{ backgroundColor: (theme) => (specialityCategories.some((c) => c.id === category.id) ? theme.palette.primary.neutral : theme.palette.background.default) }}
                  mb={1}
                  px={2}
                  key={category.id}>
                  <FormControlLabel
                    control={<Checkbox checked={specialityCategories.some((c) => c.id === category.id)} onChange={() => handleToggle(category)} />}
                    label={
                      <Box>
                        <Typography fontSize={16}>{category.name}</Typography>
                        <Typography fontSize={12} color="text.secondary">
                          {category.description}
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
      <Dialog open={!!toBeDeleted.length} onClose={() => setToBeDeleted([])}>
        <DialogTitle>Are you sure you want to unselect the speciality category?</DialogTitle>
        <DialogContent>
          {toBeDeleted.length === 1 || specialityCategoriesToVerifyDeletion.length === 1 ? (
            <Typography mb={1.5}>
              You have recorded details associated with the speciality category <b>'{specialityCategories.find((c) => c.id === toBeDeleted[0])?.name}'</b>.
            </Typography>
          ) : (
            <>
              <Typography mb={1.5}>You have recorded details associated with the speciality categories listed below:</Typography>
              <ul>
                {specialityCategoriesToVerifyDeletion.map((id) => (
                  <li key={id}>
                    <b>{specialityCategories.find((c) => c.id === id)?.name}</b>
                  </li>
                ))}
              </ul>
            </>
          )}
          <Typography mb={1.5}>
            Removing {specialityCategoriesToVerifyDeletion.length === 1 ? 'this category' : 'these categories'} will discard all related clinical and sample details already entered
            in later steps of the form.
          </Typography>
          <Typography>This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setToBeDeleted([])}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              dispatch({ type: SpecialityCategoriesReducerAction.CATEGORIES, value: specialityCategories.filter((c) => !toBeDeleted.includes(c.id)) });
              setToBeDeleted([]);
            }}>
            Unselect and discard details
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StepSpeciality;
